/**
 * Sealed conditions — a group's rule, ticked by a member who never holds it
 * (`docs/plans/screen-links.md`, Part 1).
 *
 *     browser ◀── {id, name, category, sealed: true}          no `filter`
 *     browser ──▶ ticks it  →  {field, screen, id} beside `computed`
 *     server  ──▶ per row: a boolean under `field`  →  the grid filters on it
 *
 * **Nothing is stored for one but the tick.** `activeFilterRefs` holds its id,
 * as it holds any condition's; `filter` holds only the open ones, because a
 * sealed one has no rule to put there. Its flag is ANDed in on the way to the
 * grid by `withSealedConditions` and never written back — so the section, the
 * member's layer, a Duplicate and the workspace save carry nothing that could
 * be mistaken for the rule, and nothing that names a hash only one screen can
 * answer.
 *
 * **The screen is the panel's `screenRef`.** A sealed entry reaches a panel
 * only from the library of the screen on it — `applyScreenTo` fills one from
 * the other — so the screen it belongs to is the screen being shown. A panel
 * with no `screenRef` cannot be holding one honestly, and asks for nothing.
 * A **linked** condition is the exception, and carries its own screen: it is
 * asked for by its link from whichever screen it sits on (`screen-links.ts`).
 *
 * Pure, and the island's rather than the app's, for `computed-request.ts`'s
 * reason: every host of this island must ask for the same fields it reads.
 */

import type { ScreenerConfig, ScreenerFilterNode } from "@qufolio/model";
import type { ScreenerSealedConditionRequest } from "@qufolio/transport";

import { sealedConditionField, type ScreenerFilter } from "./filter-eval.js";
import { isSealed, selectedFilterIds } from "./saved-filters.js";
import { conditionLinkKey } from "./screen-links.js";

/**
 * One ticked sealed condition, as the screen request carries it: `field` is
 * `sealedConditionField(screen, id)`, `screen` the screen whose published rule
 * it is, `id` the condition's id in that screen's library. The wire's type, so
 * the two cannot drift.
 */
export type SealedConditionRequest = ScreenerSealedConditionRequest;

/**
 * The panel's ticked sealed conditions, in library order.
 *
 * Library order rather than tick order, `applySelection`'s rule: two people
 * ticking the same boxes send the same request, so the server's channel key
 * does not split on the order somebody clicked in.
 */
export function sealedConditions(
  config: Pick<ScreenerConfig, "savedFilters" | "activeFilterRefs" | "activeFilterRef" | "screenRef">,
  /**
   * Linked conditions that resolved to nothing (`conditionLinkKey`), left out:
   * nothing could answer them, and asking would hold every row back.
   */
  missing: ReadonlySet<string> = NONE,
): SealedConditionRequest[] {
  const ticked = new Set(selectedFilterIds(config as ScreenerConfig));
  if (ticked.size === 0) return [];
  const own = config.screenRef;
  const out: SealedConditionRequest[] = [];
  for (const f of config.savedFilters ?? []) {
    if (!ticked.has(f.id)) continue;
    // **A linked condition is asked for by its link** — its source screen and
    // its id there — sealed or not (`screen-links.ts`). The server checks the
    // caller may read *that* screen, so a link grants nothing a tab would not.
    if (f.link) {
      if (missing.has(conditionLinkKey(f))) continue;
      const { screen, id } = f.link;
      out.push({ field: sealedConditionField(screen, id), screen, id });
      continue;
    }
    if (own && isSealed(f)) out.push({ field: sealedConditionField(own, f.id), screen: own, id: f.id });
  }
  return out;
}

const NONE: ReadonlySet<string> = new Set();

/**
 * The filter the grid evaluates: the open conditions, ANDed with each sealed
 * one's flag.
 *
 * A flag is a plain field condition on the hidden field, which `filter-eval`
 * reads with the history-condition rule — absent is not yet true, so a row
 * waits for the server rather than passing on a rule nobody has evaluated.
 * Returns `filter` itself, by identity, when nothing is sealed, so the common
 * case costs the grid no re-filter.
 */
export function withSealedConditions(
  filter: ScreenerFilter | undefined,
  sealed: readonly SealedConditionRequest[],
): ScreenerFilter | undefined {
  if (sealed.length === 0) return filter;
  const flags: ScreenerFilterNode[] = sealed.map(({ field }) => ({ field, op: "eq", value: true }));
  if (!filter) return flags.length === 1 ? flags[0] : { op: "all", children: flags };
  return { op: "all", children: [filter as ScreenerFilterNode, ...flags] };
}
